import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, CalendarIcon, CheckCircleIcon, PlusIcon, ArrowDownTrayIcon, TrashIcon } from '@heroicons/react/24/outline';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { format } from 'date-fns';
import { zhCN, enUS } from 'date-fns/locale';
import { useHeader } from '../../contexts/HeaderContext';
import { Dialog } from '@headlessui/react';
import { createAttendance, deleteAttendance } from '../../services/attendanceService';
import { useStudentsList, exportStudentsToCSV } from '../../hooks/useStudentsList';
import { dateToDatetimeLocal, datetimeLocalToDate } from '../../lib/utils';

interface AttendanceRecord {
    id: string;
    date: Date;
    createdByName?: string;
}

const AttendanceList: React.FC = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const courseId = queryParams.get('courseId');
    const programId = queryParams.get('programId');
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
    const [courseName, setCourseName] = useState<string>('');
    const [programName, setProgramName] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { setHeaderTheme } = useHeader();
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());
    const [isCreatingAttendance, setIsCreatingAttendance] = useState(false);
    const [programStatus, setProgramStatus] = useState<string>('');
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [selectedAttendance, setSelectedAttendance] = useState<AttendanceRecord | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    
    // 使用 useStudentsList 钩子获取学生列表
    const { students } = useStudentsList(courseId, programId);
    
    useEffect(() => {
        // 设置透明背景和白色文字
        setHeaderTheme({
            bgColor: 'bg-transparent',
            textColor: 'text-white'
        });

        return () => {
            // 恢复默认样式
            setHeaderTheme({
                bgColor: 'bg-white',
                textColor: 'text-[#142b89]'
            });
        };
    }, [setHeaderTheme]);

    useEffect(() => {
        const fetchCourseAndAttendance = async () => {
            if (!courseId) {
                setError(t('course.idMissing'));
                setLoading(false);
                return;
            }

            try {
                // 获取课程信息
                const courseDoc = await getDoc(doc(db, 'courses', courseId));
                if (!courseDoc.exists()) {
                    setError(t('course.notFound'));
                    setLoading(false);
                    return;
                }

                const courseData = courseDoc.data();
                setCourseName(i18n.language === 'zh' ? courseData.name : courseData.nameEn || courseData.name);

                // 获取项目信息（名称 + 状态，用于展示与「能否考勤/删除」的判断）
                if (programId) {
                    const programDoc = await getDoc(doc(db, 'programs', programId));
                    if (programDoc.exists()) {
                        const programData = programDoc.data();
                        setProgramName(i18n.language === 'zh' ? programData.title : programData.subtitle || programData.title);
                        setProgramStatus(programData.status || '');
                    }
                }
                
                // 获取考勤记录
                const courseRef = doc(db, 'courses', courseId);
                let attendanceQuery = query(
                    collection(db, 'attendance'),
                    where('course', '==', courseRef),
                    where('deleted', '==', false)
                );
                // 若指定了项目，则按项目过滤考勤记录，避免混入其他项目/届次的记录
                if (programId) {
                    attendanceQuery = query(attendanceQuery, where('program', '==', doc(db, 'programs', programId)));
                }

                const attendanceSnapshot = await getDocs(attendanceQuery);
                const attendanceData = attendanceSnapshot.docs.map(doc => {
                    // console.log({doc},doc.id);
                    const data = doc.data();
                    // 处理日期字段，确保它是一个有效的日期
                    let dateObj: Date;
                    if (data.date && typeof data.date.toDate === 'function') {
                        // Firestore Timestamp类型
                        dateObj = data.date.toDate();
                    } else if (data.schedule && typeof data.schedule.toDate === 'function') {
                        // 可能使用schedule字段替代date字段
                        dateObj = data.schedule.toDate();
                    } else {
                        // 如果没有合适的日期字段或无法转换，使用当前时间
                        console.warn('考勤记录缺少有效的日期字段:', doc.id);
                        dateObj = new Date();
                    }
                    
                    return {
                        id: doc.id,
                        date: dateObj,
                        createdByName: data.createdByName || ''
                    };
                });

                // 按日期排序（从新到旧）
                attendanceData.sort((a, b) => b.date.getTime() - a.date.getTime());
                setAttendanceRecords(attendanceData);
            } catch (err) {
                console.error('获取课程和考勤信息失败:', err);
                setError(t('schedule.fetchError'));
            } finally {
                setLoading(false);
            }
        };

        fetchCourseAndAttendance();
    }, [courseId, programId, t, i18n.language]);

    const handleStartAttendance = (record: AttendanceRecord) => {
        // window.open(`/#/attendance?courseId=${courseId}&date=${date.getTime()}`, '_blank');
        window.open(`/#/attendance?id=${record.id}&courseId=${courseId}&programId=${programId}&date=${record.date.getTime()}`, '_blank');
    };
    
    const handleOpenCreateDialog = () => {
        setSelectedDate(new Date());
        setIsCreateDialogOpen(true);
    };
    
    const handleCreateAttendance = async () => {
        if (!courseId || !programId || !students.length) {
            console.error('没有课程ID、项目ID或学生列表为空');
            return;
        }
        
        try {
            setIsCreatingAttendance(true);
            
            // 获取所有学生的userId（如果没有userId则使用id作为备选）
            const studentIds = students.map(student => student.id);
            
            // 记录创建人（当前登录用户）
            const currentUser = auth.currentUser;
            const creator = currentUser
                ? { id: currentUser.uid, name: currentUser.displayName || currentUser.email || '' }
                : undefined;
            
            // 创建考勤记录
            const newAttendance = await createAttendance(courseId, programId, selectedDate, studentIds, creator);
            
            // 创建成功后添加到考勤记录列表并导航到考勤详情页
            const newAttendanceRecord = {
                id: newAttendance.id,
                date: selectedDate,
                createdByName: newAttendance.createdByName || ''
            };
            
            setAttendanceRecords(prev => [newAttendanceRecord, ...prev]);
            setIsCreateDialogOpen(false);
            
            // 导航到考勤详情页
            // navigate(`/attendance?courseId=${courseId}&date=${selectedDate.getTime()}`);
        } catch (error) {
            console.error('创建考勤记录失败:', error);
        } finally {
            setIsCreatingAttendance(false);
        }
    };

    // 处理导出学生列表为CSV
    const handleExportToCSV = () => {
        if (students.length === 0) {
            alert(t('student.noStudentsToExport'));
            return;
        }
        exportStudentsToCSV(students, courseName, t);
    };

    // 处理删除考勤记录
    const handleDeleteDialogOpen = (record: AttendanceRecord, e: React.MouseEvent) => {
        e.stopPropagation(); // 阻止事件冒泡，避免触发考勤详情页面跳转
        setSelectedAttendance(record);
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteAttendance = async () => {
        if (!selectedAttendance) return;
        
        try {
            setIsDeleting(true);
            await deleteAttendance(selectedAttendance.id);
            
            // 从列表中移除已删除的记录
            setAttendanceRecords(prev => prev.filter(record => record.id !== selectedAttendance.id));
            setIsDeleteDialogOpen(false);
            setSelectedAttendance(null);
        } catch (error) {
            console.error('删除考勤记录失败:', error);
            alert(t('attendance.deleteError'));
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-900 to-blue-800 pt-20 px-4 pb-12 pt-48">
            <div className="max-w-4xl mx-auto">
                {/* 标题区域 */}
                <div className="flex items-center mb-8">
                    <button 
                        onClick={() => navigate(-1)}
                        className="text-white/80 hover:text-white mr-4"
                    >
                        <ArrowLeftIcon className="h-6 w-6" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-white">{courseName}</h1>
                        {programName && (
                            <p className="text-white/70 text-sm mt-1">{programName}</p>
                        )}
                    </div>
                    {programStatus && programStatus !== 'active' && (
                        <span className="ml-3 px-2 py-1 text-xs bg-yellow-600 text-white rounded-full">
                            {t(`program.statuses.${programStatus}`)}
                        </span>
                    )}
                </div>

                {/* 创建考勤按钮 */}
                <div className="mb-6 flex gap-2">
                    {programStatus === 'active' ? (
                        <button
                            onClick={handleOpenCreateDialog}
                            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg p-4 flex items-center justify-center transition-colors"
                        >
                            <PlusIcon className="h-5 w-5 mr-2" />
                            <span>{t('attendance.createPrompt')}</span>
                        </button>
                    ) : (
                        <div className="flex-1 bg-gray-600 text-white rounded-lg p-4 flex items-center justify-center opacity-70">
                            <span>{t('attendance.cannotCreateInactive')}</span>
                        </div>
                    )}
                    
                    {/* 导出CSV按钮 */}
                    <button
                        onClick={handleExportToCSV}
                        className="bg-green-600 hover:bg-green-700 text-white rounded-lg px-4 flex items-center justify-center transition-colors"
                        title={t('student.exportCSV')}
                    >
                        <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                        <span>{t('student.exportCSV')}</span>
                    </button>
                </div>

                {/* 学生列表 */}
                {!loading && students.length > 0 && (
                    <div className="mb-8">
                        <h2 className="text-xl font-semibold text-white mb-4">{t('attendance.studentList')}</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                            {students.map(student => (
                                <div key={student.id} className="bg-white/10 rounded-lg p-4 flex items-center">
                                    {student.avatar ? (
                                        <img 
                                            src={student.avatar} 
                                            alt={`${student.firstName} ${student.lastName}`} 
                                            className="w-12 h-12 rounded-full object-cover mr-3"
                                        />
                                    ) : (
                                        <div className="w-12 h-12 rounded-full bg-blue-500/30 flex items-center justify-center mr-3">
                                            <span className="text-white text-lg">
                                                {student.firstName?.charAt(0) || student.chineseName?.charAt(0)}
                                            </span>
                                        </div>
                                    )}
                                    <div>
                                        <div className="text-white font-medium">
                                            {`${student.firstName} ${student.lastName}`}
                                        </div>
                                        {student.chineseName && (
                                            <div className="text-blue-200 text-sm">
                                                {student.chineseName}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* 考勤记录标题 */}
                <h2 className="text-xl font-semibold text-white mb-4">{t('attendance.records')}</h2>

                {/* 内容区域 */}
                {loading ? (
                    <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
                    </div>
                ) : error ? (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                        <p className="text-red-200 text-center">{error}</p>
                    </div>
                ) : attendanceRecords.length === 0 ? (
                    <div className="bg-white/5 border border-white/10 rounded-lg p-6">
                        <p className="text-white/60 text-center">{t('attendance.noRecords')}</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {attendanceRecords.map((record, index) => (
                            <motion.div
                                key={record.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: index * 0.1 }}
                                className="bg-white/10 border border-white/10 rounded-lg p-4 flex items-center justify-between"
                                onClick={() => handleStartAttendance(record)}
                            >
                                <div className="flex items-center">
                                    <CalendarIcon className="h-5 w-5 text-blue-300 mr-3" />
                                    <div>
                                        <div className="text-white">
                                            {format(record.date, 'PPP', {
                                                locale: i18n.language === 'zh' ? zhCN : enUS
                                            })}
                                        </div>
                                        <div className="text-white/60 text-sm">
                                            {format(record.date, 'HH:mm')}
                                        </div>
                                        {record.createdByName && (
                                            <div className="text-white/40 text-xs mt-0.5">
                                                {t('attendance.createdBy')}: {record.createdByName}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                    {/* 删除按钮 */}
                                    {programStatus === 'active' && (
                                        <button
                                            onClick={(e) => handleDeleteDialogOpen(record, e)}
                                            className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center transition-colors"
                                            title={t('attendance.delete')}
                                        >
                                            <TrashIcon className="h-5 w-5" />
                                        </button>
                                    )}
                                    
                                    {/* 查看详情按钮 */}
                                    {/* <button
                                        onClick={() => handleStartAttendance(record.date)}
                                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center transition-colors"
                                    >
                                        <CheckCircleIcon className="h-5 w-5 mr-1" />
                                        <span>{t('attendance.viewDetails')}</span>
                                    </button> */}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
            
            {/* 创建考勤对话框 */}
            <Dialog 
                open={isCreateDialogOpen} 
                onClose={() => setIsCreateDialogOpen(false)}
                className="relative z-50"
            >
                <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <Dialog.Panel className="bg-white rounded-lg p-6 w-full max-w-md">
                        <Dialog.Title className="text-lg font-medium text-gray-900 mb-4">
                            {t('attendance.createAttendance')}
                        </Dialog.Title>
                        
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('attendance.selectDate')}
                            </label>
                            <input 
                                type="datetime-local" 
                                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                value={dateToDatetimeLocal(selectedDate)}
                                onChange={(e) => setSelectedDate(datetimeLocalToDate(e.target.value))}
                            />
                        </div>
                        
                        <div className="flex justify-end space-x-2">
                            <button
                                type="button"
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                                onClick={() => setIsCreateDialogOpen(false)}
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                type="button"
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400"
                                onClick={handleCreateAttendance}
                                disabled={isCreatingAttendance || students.length === 0}
                            >
                                {isCreatingAttendance ? t('common.loading') : t('common.confirm')}
                            </button>
                        </div>
                    </Dialog.Panel>
                </div>
            </Dialog>
            
            {/* 删除考勤对话框 */}
            <Dialog 
                open={isDeleteDialogOpen} 
                onClose={() => setIsDeleteDialogOpen(false)}
                className="relative z-50"
            >
                <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <Dialog.Panel className="bg-white rounded-lg p-6 w-full max-w-md">
                        <Dialog.Title className="text-xl font-medium text-gray-900 mb-2">
                            {t('attendance.confirmDelete')}
                        </Dialog.Title>
                        
                        <p className="text-gray-700 mb-6">
                            {t('attendance.deleteWarning')}
                        </p>
                        
                        <div className="flex justify-end space-x-2">
                            <button
                                type="button"
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                                onClick={() => setIsDeleteDialogOpen(false)}
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                type="button"
                                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-400"
                                onClick={handleDeleteAttendance}
                                disabled={isDeleting}
                            >
                                {isDeleting ? t('common.loading') : t('common.delete')}
                            </button>
                        </div>
                    </Dialog.Panel>
                </div>
            </Dialog>
        </div>
    );
};

export default AttendanceList; 